import { AttributesMixin, define } from "@type-r/models";
import { LocalStorageModel } from "./common";
import { authEventsQueue } from "./embedded-auth";
import { fetchJson } from "./endpoint";
import {
  AdminPermission, InmatchPermission, ParticipantRoles, PermissionLevels
} from "./permissions";
import { RelationshipPermissions, RelationshipUserInfo } from "./relationship";
import { globalStore } from "./store";

export interface UserInfo extends AttributesMixin<typeof UserInfo> {
  user_type:
    | "none"
    | "superuser"
    | "inblock"
    | "supplier"
    | "distributor"
    | "supersupplier";
}

export type ProductModule =
  | "OrderManagement"
  | "InvoiceManagement"
  | "Inmatch"
  | "CashflowForecasting"
  | "RelationshipDashboard"
  | "AllModules";



@define
export class UserInfo extends LocalStorageModel {
  static attributes = {
    // Each attribute name is the local storage key.
    user_type: String,
    loggedIn: Boolean,
    token: String,
    username: String,
    user_permissions: String,
    organization_id: String,
    relationships_user_info: [RelationshipPermissions]
  };

  private hasRelationshipPermissions(
    relationshipId: number,
    module: ProductModule,
    role: ParticipantRoles,
    permissions: PermissionLevels[],
    inclusive: boolean
  ) {
    const rPermissions = this.relationships_user_info.find(
      r => r.relationshipId === relationshipId
    );
    if (rPermissions && rPermissions.role === role) {
      const permissionLevels = rPermissions.permissions[
        module
      ] as PermissionLevels[];
      if (permissions && permissionLevels) {
        if (inclusive) {
          return permissionLevels.every(pl => permissions.includes(pl));
        } else {
          return permissionLevels.some(pl => permissions.includes(pl));
        }
      }
    }
    return false;
  }
  private hasPermissions(
    module: ProductModule,
    role: ParticipantRoles,
    permissions: PermissionLevels[],
    inclusive: boolean,
    conversationId?: number,
    relationshipId?: number,
  ) {
    let rId = relationshipId;
    const conversation = conversationId && globalStore.conversations.get(conversationId as any);
    
    if (!rId && conversation) {
      rId = conversation.relationshipId;
    }
    
    return this.hasRelationshipPermissions(
      rId,
      module,
      role,
      permissions,
      inclusive
    );
  }

  hasSomePermissions(
    module: ProductModule,
    role: ParticipantRoles,
    permissions: PermissionLevels[],
    conversationId?: number,
    relationshipId?: number
  ) {
    return this.hasPermissions(
      module,
      role,
      permissions,
      false,
      conversationId,
      relationshipId
    );
  }

  hasAllPermissions(
    module: ProductModule,
    role: ParticipantRoles,
    permissions: PermissionLevels[],
    conversationId?: number
  ) {
    return this.hasPermissions(module, role, permissions, true, conversationId);
  }

  hasSomeAdminPermissions(permissions: PermissionLevels[]) {
    const userInfo = this.relationships_user_info.get("all");
    if (userInfo) {
      const permissionLevels = userInfo.permissions[
        "AllModules"
      ] as PermissionLevels[];
      return permissionLevels.some(p => permissions.includes(p));
    }
    return false;
  }

  hasModulePermissions(module: ProductModule, conversationId) {
    if(this.canFull || this.isAdminRead || this.isAdminWrite) return true;
    const conversation = globalStore.conversations.get(conversationId as any);
    if (!conversation) return false;
    const rPermissions = this.relationships_user_info.find( r => r.relationshipId === conversation.relationshipId);
    return rPermissions && !!(rPermissions.permissions[module] || rPermissions.permissions["AllModules"]);
  }

  hasRole(role) {
    return this.relationships_user_info.length &&
      this.relationships_user_info.some(rui => rui.role === role);
  }

  hasRoleWithPermissions(module: ProductModule, role: ParticipantRoles, permissions : PermissionLevels[]) {
    return this.relationships_user_info.length &&
      this.relationships_user_info
        .some(rui => 
            rui.role === role && 
            (rui.permissions[module] as PermissionLevels[]) &&
            (rui.permissions[module] as PermissionLevels[]).some(p => permissions.includes(p)));
  }

  get isAdminRead() {
    return this.hasSomeAdminPermissions([
      AdminPermission.View,
      AdminPermission.Write
    ]);
  }

  get isAdminWrite() {
    return this.hasSomeAdminPermissions([AdminPermission.Write]);
  }

  get canFull() {
    return (
      this.user_permissions === "admin" || this.user_permissions === "full"
    );
  }

  get isSupplier() {
    const { user_type } = this;
    return user_type === "supplier" || user_type === "supersupplier";
  }

  get isSuperSupplier() {
    const { user_type } = this;
    return user_type === "supersupplier";
  }

  get isCustomerWrite() {
    const { user_type, user_permissions } = this;
    return (
      user_type === "distributor" &&
      (user_permissions === "admin" || user_permissions === "full")
    );
  }

  get isCustomerOrSupersupplier() {
    const { user_type } = this;
    return user_type === "distributor" || user_type === "supersupplier";
  }

  hasUserType(roles: UserInfo["user_type"][]) {
    return roles.includes(this.user_type);
  }

  getRelationshipId(conversationId?: number): number {
    const conversation = globalStore.conversations.get(conversationId as any);
    if (conversation) {
      return conversation.relationshipId;
    }
  }

  getRelationshipParticipantUserId(conversationId: number) {
    const conversation = globalStore.conversations.get(conversationId as any);
    if (conversation) {
      const relationship_user_info = this.relationships_user_info.find(
        r => r.relationshipId === conversation.relationshipId
      );
      return relationship_user_info ? relationship_user_info.relationshipParticipantUserId : 0;
    }
  }

  get isInmatchManagerOrAnalyst() {
    const requiredPermissions : PermissionLevels[] = [InmatchPermission.SupplierAnalyst, InmatchPermission.SupplierManager];

    const permissions = this.relationships_user_info.map(rui => rui.permissions);
    if (permissions) {
      const permissionLevels = permissions.map(p => p["Inmatch"] as PermissionLevels[]).filter(x => x);
      return permissionLevels && permissionLevels.some(permission => permission.some(p => requiredPermissions.includes(p)));
    }
    return false;
  }

  hasSomeInmatchPermission(permissionLevel : PermissionLevels) {

    const permissions = this.relationships_user_info.map(rui => rui.permissions);
    if (permissions) {
      const permissionLevels = permissions.map(p => p["Inmatch"] as PermissionLevels[]).filter(x => x);
      return permissionLevels && permissionLevels.some(permission => permission.some(p => p === permissionLevel));
    }
    return false;
  }

  isCustomer(relationshipId){
    const relInfo = this.relationships_user_info.find(x => x.relationshipId == relationshipId);
    return relInfo && relInfo.role === 'Customer';
  }

  private createKey(supplierId: number, customerId: number): string {
    return `suplierId:${supplierId},customerId:${customerId},otherId:0`;
  }
}
export function getUsersByPermissions(conversationId: number, persmissionsFilter: Object): Promise<RelationshipUserInfo[]> {
  const relationshipId = localUserInfo.getRelationshipId(conversationId);
  const url = `/api/inblock/tradetalk/v1/Relationship/${relationshipId}/users`;
  return fetchJson("POST", url, persmissionsFilter);
}

export const localUserInfo = new UserInfo();

// TradeTalk userInfo API was deprecated; use empty relationship info to avoid calling it.
authEventsQueue.on( async () => {
    localUserInfo.set( { relationships_user_info: [] }, { parse: true } );
})